import React, { useState, useMemo, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import {
    Button,
    Card,
    CardBody,
    Col,
    Container,
    Input,
    Row,
} from "reactstrap";
import EcommerceOrdersModal from "components/Common/EcommerceOrdersModal";
import TableContainer from "components/Common/TableContainer";
import WorkShiftAddEditModal from "pages/AllCommonModal/WorkShiftAddEditModal/work-shift-add-edit-modal";
import Spinners from "components/Common/Spinner";
import {
    submitUserWorkShiftFilterData,
    fetchUserWorkShiftTableAndFilterSchema,
    addUserWorkShift,
    updateUserWorkShift,
} from "model/UserWorkShiftModel";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import Loaders from "components/Common/Loaders";
import { usePermissionContext } from "libs/context/PermissionContext";
import { useCommonFilterOptions } from "libs/context/CommonFilterOptionsContext";
import { DEFAULT_PAGE_SIZE } from "helpers/GeneralHelper";
import { useCommonTableSettingsPageSize } from "hooks/useSettingsReadyTablePageSize";



const WorkShift = () => {
    const { permissionData } = usePermissionContext() || {};
    const { fetchMetadataSchema } = useCommonFilterOptions();
    const { pageSize: settingsPageSize, settingsReady } = useCommonTableSettingsPageSize("WorkShift");
    const initialFilterFetchedRef = useRef(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [workShiftAddEditModalOpen, setWorkShiftAddEditModalOpen] = useState(false);
    const [modal1, setModal1] = useState(false);
    const [workShiftData, setWorkShiftData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editableData, setEditableData] = useState(null);
    const [tableMetaData, setTableMetaData] = useState([]);
    const [requestPayload, setRequestPayload] = useState({ pageNumber: 1, pageSize: DEFAULT_PAGE_SIZE });
    const [paginationMeta, setPaginationMeta] = useState({
        pageNumber: 1,
        pageSize: DEFAULT_PAGE_SIZE,
        totalPages: 0,
        totalRecords: 0,
    });
    const toggleViewModal = () => setModal1(!modal1);
    const formatMinuteToTime = (minuteValue) => {
        const totalMinutes = Number(minuteValue);
        if (Number.isNaN(totalMinutes) || totalMinutes < 0) return "";
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
    };

    const formatDateToMMDDYYYY = (value) => {
            if (!value) return "";
            const parsedDate = dayjs(value);
            if (!parsedDate.isValid()) return value;
            return parsedDate.format("MM-DD-YYYY");
        };

    const formatTimeToMinute = (timeValue) => {
        const raw = String(timeValue || "");
        if (!raw.includes(":")) return 0;
        const [h, m] = raw.split(":");
        const hours = Number(h);
        const minutes = Number(m);
        if (Number.isNaN(hours) || Number.isNaN(minutes)) return 0;
        return (hours * 60) + minutes;
    };

    const defaultTableMetaData = [
        { Header: "ID", accessor: "id" },
        { Header: "Work Shift Title", accessor: "workShiftTitle" },
        { Header: "Work Shift In Hour", accessor: "workShiftInHour" },
        { Header: "Work Shift In Minute", accessor: "workShiftInMinute" },
        { Header: "Work Shift Time Display", accessor: "workShiftTimeDisplay" },
        { Header: "Work Shift Update By User Id", accessor: "workShiftUpdateByUserId" },
        { Header: "Work Shift Update On", accessor: "workShiftUpdateOn" },
    ];

    const getWorkShiftTableSchema = async () => {
        const response = await fetchMetadataSchema("metadata:userWorkShift", () =>
            fetchUserWorkShiftTableAndFilterSchema({})
        );
        const columns = response?.data?.table?.columns || [];
        if (Array.isArray(columns) && columns.length) {
            setTableMetaData(
                columns.map((col) => ({
                    Header: col?.title,
                    accessor: col?.field,
                    type: col?.type,
                }))
            );
            return;
        }
        setTableMetaData(defaultTableMetaData);
    };

    const filteredData = useMemo(() => {
        return workShiftData;
    }, [workShiftData]);

    const workShiftActionPermissions = useMemo(
        () => ({
            canCreate: Boolean(permissionData?.UserWorkShiftAccess),
            canEdit: Boolean(permissionData?.UserWorkShiftAccess),
        }),
        [permissionData]
    );

    const hasAnyWorkShiftRowAction = useMemo(
        () => workShiftActionPermissions.canEdit,
        [workShiftActionPermissions]
    );

    const getWorkShiftList = async (payloadOverrides = {}) => {
        setLoading(true);
        const payload = {
            ...requestPayload,
            ...payloadOverrides,
        };
        const response = await submitUserWorkShiftFilterData(payload);
        const responseData = response?.data || {};
        const listData = responseData?.records || responseData || [];
        const mappedData = (Array.isArray(listData) ? listData : []).map((item) => ({
            ...item,
            id: item?.id || item?._id || "",
            workShiftTitle: item?.workShiftTitle || item?.title || item?.name || "",
            workShiftInHour: Number(item?.workShiftInHour ?? 0),
            workShiftInMinute: Number(item?.workShiftInMinute ?? 0),
            workShiftTimeDisplay:
                item?.workShiftTimeDisplay ||
                item?.loginTime ||
                item?.logInTime ||
                item?.time ||
                formatMinuteToTime(item?.workShiftInMinute),
            workShiftUpdateByUserId: item?.workShiftUpdateByUserId || item?.updatedByUserId || "",
            workShiftUpdateOn: formatDateToMMDDYYYY(item?.workShiftUpdateOn || item?.updatedOn || ""),

        }));

        if (response) {
            setWorkShiftData(mappedData);
            setPaginationMeta({
                pageNumber: Number(responseData?.pageNumber) || Number(payload?.pageNumber) || 1,
                pageSize: Number(responseData?.pageSize) || Number(payload?.pageSize) || DEFAULT_PAGE_SIZE,
                totalPages: Number(responseData?.totalPages) || 0,
                totalRecords: Number(responseData?.totalRecords) || 0,
            });
            setRequestPayload(payload);
        } else {
            setWorkShiftData([]);
        }
        setLoading(false);
    };

    useEffect(() => {
        getWorkShiftTableSchema();
    }, []);

    useEffect(() => {
        if (!settingsReady || initialFilterFetchedRef.current) return;
        initialFilterFetchedRef.current = true;
        const pageSize = Number(settingsPageSize) || DEFAULT_PAGE_SIZE;
        setRequestPayload((previous) => ({ ...previous, pageSize }));
        setPaginationMeta((previous) => ({ ...previous, pageSize }));
        getWorkShiftList({ pageNumber: 1, pageSize });
    }, [settingsReady, settingsPageSize]);

    const handleSearchSubmit = async (value) => {
        const trimmedValue = value?.trim() || "";
        await getWorkShiftList({
            ...requestPayload,
            pageNumber: 1,
            nameContains: trimmedValue,
        });
    };

    const handleEdit = (row) => {
        setEditableData(row || null);
        setWorkShiftAddEditModalOpen(true);
    };

    const handleAddEditSubmit = async (workShiftFormData = {}) => {
        const loginTime = String(workShiftFormData?.loginTime || "").trim();
        const workShiftInMinute = formatTimeToMinute(loginTime);
        const payload = {
            id: workShiftFormData?.id || "",
            workShiftTitle: String(workShiftFormData?.title || "").trim(),
            workShiftTimeDisplay: loginTime,
            workShiftInHour: 0,
            workShiftInMinute,
        };
        const response = payload?.id
            ? await updateUserWorkShift(payload?.id, payload)
            : await addUserWorkShift(payload);

        if (response?.success || response?.isSuccess) {
            toast.success(response?.message || "Work Shift saved successfully");
            setWorkShiftAddEditModalOpen(false);
            setEditableData(null);
            await getWorkShiftList({ pageNumber: 1, pageSize: paginationMeta?.pageSize || DEFAULT_PAGE_SIZE });
            return;
        }
        toast.error(response?.message || "Unable to save Work Shift");
    };

    const handlePageChange = async (page) => {
        await getWorkShiftList({ pageNumber: page });
    };

    const handleRowsPerPageChange = async (rows) => {
        await getWorkShiftList({ pageNumber: 1, pageSize: rows });
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <EcommerceOrdersModal isOpen={modal1} toggle={toggleViewModal} />

                    <Card>
                        <CardBody>
                            <div className="d-flex justify-content-between align-items-center">
                                <h5 className="fw-bold mb-3">Work Shift</h5>
                                {workShiftActionPermissions.canCreate ? (
                                    <Col xs="12" md="4" className="text-md-end mt-2 mt-md-0">
                                        <Button
                                            color="primary"
                                            onClick={() => {
                                                setEditableData(null);
                                                setWorkShiftAddEditModalOpen(true);
                                            }}
                                        >
                                            + Create
                                        </Button>
                                    </Col>
                                ) : null}
                            </div>
                            <hr />

                            <Row className="align-items-center g-2 mb-4">
                                <Col xs="12" md="8">
                                    <div className="d-flex border rounded w-100 category-search-input">
                                        <Input
                                            type="text"
                                            placeholder="Search by title"
                                            className="border-0"
                                            value={searchQuery}
                                            onChange={(e) => {
                                                const nextValue = e?.target?.value || "";
                                                setSearchQuery(nextValue);
                                                if (!nextValue.trim()) {
                                                    handleSearchSubmit("");
                                                }
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    handleSearchSubmit(searchQuery?.trim());
                                                }
                                            }}
                                        />
                                        <Button
                                            className="search-icon-button"
                                            onClick={() => handleSearchSubmit(searchQuery?.trim())}
                                        >
                                            <i className="fa fa-search"></i>
                                        </Button>
                                    </div>
                                </Col>
                            </Row>

                            {!loading && tableMetaData?.length ? (
                                <TableContainer
                                    columns={tableMetaData}
                                    data={filteredData}
                                    isActionRequired={hasAnyWorkShiftRowAction}
                                    onEdit={
                                        workShiftActionPermissions.canEdit ? handleEdit : undefined
                                    }
                                    isEditAccess={workShiftActionPermissions.canEdit}
                                    isCheckboxLeftSide={false}
                                    isPagination={true}
                                    currentPage={paginationMeta?.pageNumber}
                                    rowsPerPage={paginationMeta?.pageSize}
                                    totalPages={paginationMeta?.totalPages}
                                    totalRecords={paginationMeta?.totalRecords}
                                    onPageChange={handlePageChange}
                                    onPageSizeChange={handleRowsPerPageChange}
                                    pageName={"WorkShift"}
                                />
                            ) : (
                                // <Card className="border mb-3">
                                //     <CardBody>
                                //         <Spinners setLoading={setLoading} />
                                //     </CardBody>
                                // </Card>
                                <Loaders />
                            )}
                        </CardBody>
                    </Card>

                    <WorkShiftAddEditModal
                        workShiftAddEditModalOpen={workShiftAddEditModalOpen}
                        setWorkShiftAddEditModalOpen={setWorkShiftAddEditModalOpen}
                        editableData={editableData}
                        onSubmit={handleAddEditSubmit}
                    />
                </Container>
            </div>
        </React.Fragment>
    );
};

WorkShift.propTypes = {
    orders: PropTypes.array,
    onGetOrders: PropTypes.func,
};

export default withRouter(WorkShift);
